import React from "react";
import { useLocationContext } from "../../../contexts/LocationContext";
import { useTenantContext } from "../../../contexts/TenantContext";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";

const EmployeeFilters = ({
  searchTerm,
  onSearchChange,
  selectedRole,
  onRoleChange,
  selectedDepartment,
  onDepartmentChange,
  selectedEmploymentType,
  onEmploymentTypeChange,
  selectedEligibilityStatus,
  onEligibilityStatusChange,
  jobRoles = [], // Receive job roles from parent
}) => {
  const employmentTypeOptions = [
    { label: "All Types", value: "" },
    { label: "Full-time", value: "Full-time" },
    { label: "Part-time", value: "Part-time" },
    { label: "Casual", value: "Casual" },
  ];

  const eligibilityOptions = [
    { label: "All Statuses", value: "" },
    { label: "Eligible", value: "Eligible" },
    { label: "Not Eligible", value: "Not Eligible" },
  ];

  // Get unique departments from job roles
  const departments = [
    ...new Set(jobRoles.map((r) => r.department).filter(Boolean)),
  ].sort();

  const handleClearFilters = () => {
    onSearchChange("");
    onRoleChange("");
    onDepartmentChange("");
    onEmploymentTypeChange("");
    onEligibilityStatusChange("");
  };

  const hasActiveFilters =
    searchTerm ||
    selectedRole ||
    selectedDepartment ||
    selectedEmploymentType ||
    selectedEligibilityStatus;

  // Get count of active filters
  const activeFilterCount = [
    searchTerm,
    selectedRole,
    selectedDepartment,
    selectedEmploymentType,
    selectedEligibilityStatus,
  ].filter(Boolean).length;

  // Get labels for active filters
  const getActiveFilterLabels = () => {
    const labels = [];
    if (searchTerm) labels.push(`Search: "${searchTerm}"`);
    if (selectedRole) {
      const role = jobRoles.find((r) => r.id === selectedRole);
      labels.push(`Role: ${role?.name || selectedRole}`);
    }
    if (selectedDepartment) labels.push(`Department: ${selectedDepartment}`);
    if (selectedEmploymentType) labels.push(`Type: ${selectedEmploymentType}`);
    if (selectedEligibilityStatus)
      labels.push(`Status: ${selectedEligibilityStatus}`);
    return labels;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      {/* Single Row of Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search - Takes more space */}
        <div className="flex-1 min-w-[250px]">
          <div className="relative">
            <Icon
              name="Search"
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10"
            />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="input !pl-10 w-full"
            />
          </div>
        </div>

        {/* Role Filter */}
        <div className="min-w-[180px]">
          <select
            value={selectedRole || ""}
            onChange={(e) => onRoleChange(e.target.value)}
            className="input w-full !pr-10"
          >
            <option value="">All Roles</option>
            {jobRoles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
        </div>

        {/* Department Filter */}
        <div className="min-w-[180px]">
          <select
            value={selectedDepartment || ""}
            onChange={(e) => onDepartmentChange(e.target.value)}
            className="input w-full !pr-10"
          >
            <option value="">All Departments</option>
            {departments.map((dept) => (
              <option key={dept} value={dept}>
                {dept}
              </option>
            ))}
          </select>
        </div>

        {/* Employment Type Filter */}
        <div className="min-w-[150px]">
          <select
            value={selectedEmploymentType || ""}
            onChange={(e) => onEmploymentTypeChange(e.target.value)}
            className="input w-full !pr-10"
          >
            {employmentTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Eligibility Filter */}
        <div className="min-w-[150px]">
          <select
            value={selectedEligibilityStatus || ""}
            onChange={(e) => onEligibilityStatusChange(e.target.value)}
            className="input w-full !pr-10"
          >
            {eligibilityOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Clear Filters Button - Only show when filters are active */}
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="btn btn-outline btn-sm whitespace-nowrap"
          >
            <Icon name="X" size={16} />
            Clear
          </button>
        )}
      </div>

      {/* Active Filter Badges */}
      {hasActiveFilters && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-muted-foreground">
              {activeFilterCount} active:
            </span>
            {getActiveFilterLabels().map((label, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full"
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeFilters;
